/**
 * ReadAloudMiniPlayer — the reader's hand on a readout in progress.
 *
 * SECOND PASS, 2026-09-10 (Miles, on the live build): "when a user clicks on the
 * read aloud button it seems to do nothing … the wait needs to be much less";
 * "it's confusing to the user because it doesn't really slide but it does go
 * faster or slower"; "remove the sections for smaller items (pin this for later
 * because this is not a good user experience right now)"; "every aspect of this
 * needs to be an extremely intuitive user experience … from beginning to the
 * end". Three things changed, and the root cause of all three was the same: a
 * seven-control transport had been crammed into a 13 px meta line.
 *
 *  1. IT IS ITS OWN ROW under the message now, not an item in the meta line.
 *     That is what buys room for words, a progress bar and a slider with real
 *     length — none of which fit beside a timestamp.
 *  2. PRESSING IT ALWAYS VISIBLY DOES SOMETHING. The bar appears in the same
 *     frame as the click and SAYS "getting the voice ready…" over a moving
 *     band. Before, the only answer to a press was a 13 px speaker turning gold
 *     plus a row of DISABLED buttons and a meaningless "1 / 1" — which reads as
 *     broken, not as busy. (The live harness never once observed the loading
 *     state: probe.json `sawLoadingState: false`.)
 *  3. NO SECTION BUTTONS. Segments are how the SERVER chunks text for the
 *     voice; on the ordinary message there is exactly one, so "previous/next
 *     section" sat permanently dead and the counter read "1 / 1". Progress is
 *     shown as a bar and a clock instead — how far along, and how long — which
 *     is what a person actually wants from a player. The mechanism is parked,
 *     not deleted (see seekReadAloudSegment).
 *
 * Chrome only (D9): every string here is a label, and each control is a
 * straight call into the player's own command surface — nothing here decides
 * anything.
 */

import React from 'react';
import {
  ReadAloudControlButton,
  ReadAloudControls,
  ReadAloudMeter,
  ReadAloudPrimaryButton,
  ReadAloudProgressFill,
  ReadAloudProgressIndeterminate,
  ReadAloudProgressTrack,
  ReadAloudSpeedGroup,
  ReadAloudSpeedSlider,
  ReadAloudStatus,
} from './styles';
import { readAloudPlayerCopy } from '../../services/readAloud/readAloudCopy';
import {
  READ_ALOUD_MAX_RATE,
  READ_ALOUD_MIN_RATE,
  READ_ALOUD_RATE_STEP,
  restartReadAloud,
  setReadAloudRate,
  stepReadAloudRate,
  toggleReadAloudPause,
  type ReadAloudState,
} from '../../services/readAloud/player';

const PauseIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="9" y1="5" x2="9" y2="19" />
    <line x1="15" y1="5" x2="15" y2="19" />
  </svg>
);

const PlayIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <path d="M7 5l12 7-12 7z" />
  </svg>
);

const RestartIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12a9 9 0 1 0 3-6.7" />
    <polyline points="3 4 3 10 9 10" />
  </svg>
);

const MinusIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const PlusIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="5" y1="12" x2="19" y2="12" />
    <line x1="12" y1="5" x2="12" y2="19" />
  </svg>
);

export interface ReadAloudMiniPlayerProps {
  state: ReadAloudState;
  /** How far through the whole readout, 0..1. */
  progress: number;
  /** Seconds of speech played so far. */
  elapsedSeconds: number;
  /** The voice ran out of ready audio mid-readout; the next part is being made. */
  preparingNext?: boolean;
  rate: number;
  /** The message's own language, so a Spanish message's controls read in Spanish. */
  language?: string;
}

/** A percentage the slider can carry, in whole ten-point steps. */
const PERCENT_MIN = Math.round(READ_ALOUD_MIN_RATE * 100);
const PERCENT_MAX = Math.round(READ_ALOUD_MAX_RATE * 100);
const PERCENT_STEP = Math.round(READ_ALOUD_RATE_STEP * 100);

export const ReadAloudMiniPlayer: React.FC<ReadAloudMiniPlayerProps> = ({
  state,
  progress,
  elapsedSeconds,
  preparingNext,
  rate,
  language,
}) => {
  if (state === 'idle') return null;
  const copy = readAloudPlayerCopy(language);
  const paused = state === 'paused';
  // There is no sound yet — the voice is still being made. Everything that
  // would act on audio waits, but the bar SAYS so and keeps moving.
  const preparing = state === 'loading';
  // A seam the pipeline did not cover: the reader is mid-readout and the next
  // part is still rendering. The bar says so rather than going quiet, and the
  // controls stay live — there is audio behind them to start over from.
  const atSeam = !preparing && preparingNext === true;
  const percent = Math.round(rate * 100);

  return (
    <ReadAloudControls data-read-aloud-player="" role="group" aria-label={copy.player}>
      <ReadAloudPrimaryButton
        type="button"
        data-read-aloud-playpause=""
        data-read-aloud-paused={paused ? 'true' : 'false'}
        aria-label={paused ? copy.play : copy.pause}
        title={paused ? copy.play : copy.pause}
        disabled={preparing}
        onClick={() => toggleReadAloudPause()}
      >
        {paused ? <PlayIcon /> : <PauseIcon />}
      </ReadAloudPrimaryButton>

      <ReadAloudControlButton
        type="button"
        data-read-aloud-restart=""
        aria-label={copy.restart}
        title={copy.restart}
        disabled={preparing}
        onClick={() => restartReadAloud()}
      >
        <RestartIcon />
      </ReadAloudControlButton>

      <ReadAloudProgressTrack
        data-read-aloud-progress={preparing || atSeam ? 'preparing' : String(Math.round(progress * 100))}
        role="progressbar"
        aria-label={copy.progress}
        aria-valuemin={0}
        aria-valuemax={100}
        {...(preparing || atSeam ? {} : { 'aria-valuenow': Math.round(progress * 100) })}
      >
        {preparing || atSeam
          ? <ReadAloudProgressIndeterminate />
          : <ReadAloudProgressFill $fraction={progress} />}
      </ReadAloudProgressTrack>

      {/* The one thing the old player never did: say, in words, that a press
          landed and the voice is coming. */}
      <ReadAloudStatus data-read-aloud-position="">
        {preparing ? copy.preparing : atSeam ? copy.preparingNext : copy.clock(elapsedSeconds)}
      </ReadAloudStatus>

      <ReadAloudSpeedGroup>
        <ReadAloudControlButton
          type="button"
          data-read-aloud-slower=""
          aria-label={copy.slower}
          title={copy.slower}
          disabled={percent <= PERCENT_MIN}
          onClick={() => stepReadAloudRate(-1)}
        >
          <MinusIcon />
        </ReadAloudControlButton>
        <ReadAloudSpeedSlider
          type="range"
          data-read-aloud-speed=""
          min={PERCENT_MIN}
          max={PERCENT_MAX}
          step={PERCENT_STEP}
          value={percent}
          aria-label={copy.speed}
          aria-valuetext={copy.ratePercent(rate)}
          title={`${copy.speed} · ${copy.ratePercent(rate)}`}
          onChange={(e) => setReadAloudRate(Number(e.target.value) / 100)}
        />
        <ReadAloudControlButton
          type="button"
          data-read-aloud-faster=""
          aria-label={copy.faster}
          title={copy.faster}
          disabled={percent >= PERCENT_MAX}
          onClick={() => stepReadAloudRate(1)}
        >
          <PlusIcon />
        </ReadAloudControlButton>
        <ReadAloudMeter data-read-aloud-rate="">{copy.rateLabel(rate)}</ReadAloudMeter>
      </ReadAloudSpeedGroup>
    </ReadAloudControls>
  );
};

export default ReadAloudMiniPlayer;
